import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import PageHeader from "../-components/PageHeader";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";
import { makeIntComma, toastErrorSyle } from "hooks/utils/util";
import { useGetConsoleDriverSchedules, useDeleteConsoleDriverSchedule } from "hooks/console/useConsoleDriverSchedule";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";

const theadStyle = "text-[#222222] text-[16px] text-center";

// 날짜 표시: 단일일은 그대로, 기간이면 '시작일 ~ 종료일 (N일)'
const formatPeriod = (row: { work_start_date?: string; work_end_date?: string; day_count?: number; work_date?: string }) => {
    const s = row.work_start_date;
    const e = row.work_end_date;
    if (!s || s === e) return s || row.work_date || "";
    return `${s} ~ ${e} (${row.day_count}일)`;
};

export default function ConsoleDriverSchedules() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState<{
        searchtxt: string;
        work_start_date: string;
        work_end_date: string;
        page: number;
        limit: string;
    }>({
        searchtxt: "",
        work_start_date: "",
        work_end_date: "",
        page: 1,
        limit: "100",
    });
    // 검색어 디바운스 — 키 입력마다 호출하지 않고, 타이핑이 멈춘 뒤에만 조회
    const [debouncedSearchtxt, setDebouncedSearchtxt] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchtxt(searchForm.searchtxt), 350);
        return () => clearTimeout(timer);
    }, [searchForm.searchtxt]);
    const { data, refetch, isLoading } = useGetConsoleDriverSchedules({
        searchtxt: debouncedSearchtxt || undefined,
        work_start_date: searchForm.work_start_date || undefined,
        work_end_date: searchForm.work_end_date || undefined,
        page: searchForm.page,
        limit: searchForm.limit,
    });
    const { mutate: deleteSchedule } = useDeleteConsoleDriverSchedule();

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        setSearchForm(prev => ({ ...prev, page: Number(page) }));
    }, [searchParams]);

    const deleteRow = (close: () => void) => {
        deleteSchedule(
            { schedule_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    close();
                    setSelectedRows([]);
                    refetch();
                },
                onError: (error: any) => toast.error(error.message, toastErrorSyle),
            },
        );
    };

    // 묶음(연속 기간) 단위 선택 — 묶음의 모든 schedule_no를 토글
    const toggleGroup = (scheduleNos: number[]) => {
        setSelectedRows(prev => {
            const allIn = scheduleNos.every(n => prev.includes(n));
            if (allIn) return prev.filter(n => !scheduleNos.includes(n));
            return Array.from(new Set([...prev, ...scheduleNos]));
        });
    };

    const allScheduleNos = (data?.data_list ?? []).flatMap(item => item.schedule_nos);
    const allSelected = allScheduleNos.length > 0 && allScheduleNos.every(n => selectedRows.includes(n));

    const allCheck = (checked: boolean) => {
        setSelectedRows(checked ? allScheduleNos : []);
    };

    return (
        <>
            <Seo type="console" pageTitle="일자별 입고지 기사 관리" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="일자별 입고지 기사 관리" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form onSubmit={e => e.preventDefault()}>
                                <div className="flex items-center gap-2 justify-end flex-wrap">
                                    {/* 갯수 */}
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger className="relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[110px] border border-[#E1E2E4] bg-white h-[50px]">
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="100">100개</SelectItem>
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {/* 날짜 기간 (시작일 ~ 종료일) */}
                                    <div className="flex items-center gap-[6px]">
                                        <input
                                            type="date"
                                            value={searchForm.work_start_date}
                                            max={searchForm.work_end_date || undefined}
                                            onChange={e =>
                                                setSearchForm(prev => ({ ...prev, work_start_date: e.target.value, page: 1 }))
                                            }
                                            onClick={e => {
                                                try {
                                                    (e.currentTarget as HTMLInputElement).showPicker?.();
                                                } catch {}
                                            }}
                                            className="border border-[#dddddd] rounded-[5px] bg-white px-[16px] h-[50px] cursor-pointer"
                                        />
                                        <span className="text-[#666666]">~</span>
                                        <input
                                            type="date"
                                            value={searchForm.work_end_date}
                                            min={searchForm.work_start_date || undefined}
                                            onChange={e =>
                                                setSearchForm(prev => ({ ...prev, work_end_date: e.target.value, page: 1 }))
                                            }
                                            onClick={e => {
                                                try {
                                                    (e.currentTarget as HTMLInputElement).showPicker?.();
                                                } catch {}
                                            }}
                                            className="border border-[#dddddd] rounded-[5px] bg-white px-[16px] h-[50px] cursor-pointer"
                                        />
                                    </div>
                                    {/* 검색어 — 항구명/입고지명/기사명 통합 */}
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[340px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchForm.searchtxt}
                                            onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value, page: 1 }))}
                                            placeholder="항구명, 입고지, 기사명으로 검색하세요."
                                            className="text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]"
                                        />
                                        <ReadingGlasses />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" checked={allSelected} onChange={e => allCheck(e.target.checked)} />
                                            <span>전체선택</span>
                                        </label>
                                        <label className="text-[#222222] text-[18px]">
                                            선택한 항목 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                        </label>
                                    </div>
                                    <ConfirmDialog
                                        title="일자별 기사배정 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 항목을 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "8%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = (data?.total_count ?? 0) - (searchForm.page - 1) * Number(searchForm.limit) - rowIndex;
                                                return (
                                                    <label
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={row.schedule_nos.every((n: number) => selectedRows.includes(n))}
                                                            onChange={() => toggleGroup(row.schedule_nos)}
                                                        />
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },
                                        { title: "항구명", width: "12%", key: "port_name", className: twMerge(theadStyle, "text-[#666666]") },
                                        { title: "입고지명", width: "14%", key: "warehouse_name", className: twMerge(theadStyle, "text-[#666666]") },
                                        {
                                            title: "날짜",
                                            width: "30%",
                                            key: "work_date",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps) => (
                                                <p className="text-center text-[#666666] whitespace-nowrap">{formatPeriod(row)}</p>
                                            ),
                                        },
                                        { title: "담당기사", width: "11%", key: "driver_name", className: twMerge(theadStyle, "text-[#666666]") },
                                        { title: "전화번호", width: "13%", key: "phone", className: twMerge(theadStyle, "text-[#666666]") },
                                        { title: "차량번호", width: "12%", key: "car_no", className: twMerge(theadStyle, "text-[#666666]") },
                                    ]}
                                    tbody={data?.data_list || []}
                                    uniqueKeyName="schedule_no"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${row.schedule_no}`, { state: { page: searchParams.get("page") ?? "1" } })}
                                />

                                <div className="flex justify-end mt-5">
                                    <button
                                        className="bg-black text-white rounded-[5px] px-4 py-2"
                                        onClick={() => navigate("create", { state: { page: searchParams.get("page") ?? "1" } })}
                                    >
                                        작성하기
                                    </button>
                                </div>
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
